import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import CheckedBoxIcon from "../icons/CheckboxIcon";
import { useSubmitInquiryMutation } from "../../services/public/submitInquiry";
import Modal from "./Modal";
import InputFieldFloat from "../inputs/InputFieldFloat";
import InputTextAreaFloat from "../inputs/InputTextAreaFloat";
import PhoneInput from "../inputs/PhoneInput";
import { useTranslation } from "react-i18next";
import { COUNTRY_OPTIONS } from "../../constants/landingPage";
import { useGetLocationDataQuery } from "../../services/public/location";
import { Link } from "react-router-dom";

const RequestDemoModal = ({ isOpened, onClose, planId }) => {
  const { t } = useTranslation('signup');

  const { data: locationData } = useGetLocationDataQuery();
  const selectedCountry = COUNTRY_OPTIONS.find(c => c.label === locationData?.country?.toLowerCase()) || COUNTRY_OPTIONS[0];

  const validationSchema = yup.object({
    firstName: yup.string().required(t('first_name_required')),
    lastName: yup.string().required(t('last_name_required')),
    email: yup.string().email(t('email_invalid')).required(t('email_required')),
    phone_number: yup.string().required(t('phone_required')).matches(/^[0-9+\-\s]+$/, t('phone_invalid')),
    country_code: yup.string().required(t('country_code_required')),
    companyName: yup.string().required(t('company_name_required')),
    inquiry: yup.string().required(t('inquiry_required')).min(10, t('inquiry_min')),
    termsAccepted: yup.boolean().oneOf([true], t('terms_must_be_accepted')),
    seatCount: yup.number().required(t("seat_count_required")),
  });
  
  const [submitError, setSubmitError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const [submitInquiry, { isLoading: isSubmitting }] = useSubmitInquiryMutation();

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone_number: "",
      country_code: selectedCountry?.value || "+965",
      companyName: "",
      inquiry: "",
      termsAccepted: false,
      seatCount: "",
      planId: planId,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitError("");
      if(isSubmitting) return;
      try {
        const inquiryData = {
          first_name: values.firstName,
          last_name: values.lastName,
          email: values.email,
          phone: values.phone_number,
          country_code: values.country_code,
          org_name: values.companyName,
          plan_name: "enterprise",
          details: values.inquiry || "",
          plan_id: values.planId,
          seat_count: values.seatCount,
        }
        await submitInquiry(inquiryData).unwrap();
        resetForm();
        setIsSuccess(true);
      } catch (error) {
        setSubmitError(
          error?.data?.message ||
          t("demo_submit_error")
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpened) {
      setIsSuccess(false);
      formik.resetForm();
      setSubmitError("");
    }
    
    // Bootstrap modal logic
    const el = document.getElementById("requestDemoModal");
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (isOpened) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [isOpened]);

  // Handle hidden event to call onClose
  useEffect(() => {
    const el = document.getElementById("requestDemoModal");
    if (!el) return;
    const handleHidden = () => onClose?.();
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, [onClose]);

  return (
    <Modal
      id="requestDemoModal"
      isOpened={isOpened}
      onClose={onClose}
      hasCloseBtn={!isSuccess}
      isVerticallyCentered
    >
      <div className="rd-page" style={{ background: "transparent", padding: 0 }}>
        {!isSuccess ? (
          <div className="rd-hero__container" style={{ margin: 0, padding: 0, minHeight: "auto", boxShadow: "none" }}>
            <div className="rd-hero__form-col">
              <form
                className="rd-form autofill-wrapper-dark"
                onSubmit={(e) => { e.preventDefault(); formik.handleSubmit(e); }}
                noValidate
              >
                <InputFieldFloat 
                  name="firstName" 
                  type="text" 
                  label={t("first_name")} 
                  value={formik.values.firstName} 
                  placeholder={t("first_name")} 
                  required={true} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur} 
                  error={formik.errors.firstName}
                  touched={formik.touched.firstName}
                  floating={true}
                />

                <InputFieldFloat 
                  name="lastName" 
                  type="text" 
                  label={t("last_name")} 
                  value={formik.values.lastName} 
                  placeholder={t("last_name")} 
                  required={true} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur} 
                  error={formik.errors.lastName}
                  touched={formik.touched.lastName}
                  floating={true}
                />

                <InputFieldFloat 
                  name="email" 
                  type="email" 
                  label={t("email")} 
                  value={formik.values.email} 
                  placeholder={t("email")} 
                  required={true} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur} 
                  error={formik.errors.email}
                  touched={formik.touched.email}
                  floating={true}
                />

                <div className="rd-phone">
                  <InputFieldFloat 
                    name="phone_number" 
                    type="tel" 
                    label={t("phone")} 
                    value={formik.values.phone_number} 
                    placeholder="00000000000"
                    required={true} 
                    onChange={formik.handleChange} 
                    onBlur={formik.handleBlur} 
                    error={formik.errors.phone_number}
                    touched={formik.touched.phone_number}
                    floating={true}
                  />
                  <PhoneInput 
                    name="country_code" 
                    label={t("country_code")} 
                    value={formik.values.country_code} 
                    onChange={(value) => formik.setFieldValue("country_code", value)} 
                  />
                </div>
                
                <InputFieldFloat 
                  name="companyName" 
                  type="text" 
                  label={t("company_organization")} 
                  value={formik.values.companyName} 
                  placeholder={t("company_organization")} 
                  required={true} 
                  onChange={formik.handleChange} 
                  onBlur={formik.handleBlur} 
                  error={formik.errors.companyName}
                  touched={formik.touched.companyName}
                  floating={true}
                />

                <InputFieldFloat 
                  name="seatCount" 
                  type="text" 
                  label={t("seat_count")} 
                  value={formik.values.seatCount} 
                  placeholder={t("seat_count")} 
                  required={true} 
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || "";
                    formik.setFieldValue("seatCount", value);
                  }} 
                  onBlur={formik.handleBlur} 
                  error={formik.errors.seatCount}
                  touched={formik.touched.seatCount}
                  floating={true}
                />

                <InputTextAreaFloat
                  name="inquiry"
                  label={t("any_questions")}
                  value={formik.values.inquiry}
                  placeholder={t("inquiry_placeholder")}
                  required={true}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  touched={formik.touched.inquiry}
                  error={formik.errors.inquiry}
                  floating={true}
                  rows={4}
                />

                <div className="rd-terms">
                  <div
                    className={`rd-terms__box${formik.values.termsAccepted ? " rd-terms__box--checked" : ""}`}
                    onClick={() => formik.setFieldValue("termsAccepted", !formik.values.termsAccepted)}
                  >
                    {formik.values.termsAccepted && <CheckedBoxIcon />}
                  </div>
                  <label
                    className="rd-terms__label"
                  >
                    <Link to="/terms" key="terms" target="_blank">{t("agree_to_terms_and_conditions")}</Link>
                  </label>
                </div>
                {formik.touched.termsAccepted && formik.errors.termsAccepted && (
                  <div className="rd-field__error">{formik.errors.termsAccepted}</div>
                )}

                {submitError && (
                  <div className="rd-submit-error">{submitError}</div>
                )}
  
                <button
                  type="submit"
                  className="rd-submit-btn"
                  disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
                >
                  {formik.isSubmitting ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  ) : t("submit")}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="text-center py-5 d-flex flex-column align-items-center justify-content-center">
            <div className="status-icon success">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
              >
                <rect width="80" height="80" rx="40" fill="#51CAAD" />
                <path
                  d="M22 40.1579L33.3684 52L57.3684 28"
                  stroke="white"
                  strokeWidth="6"
                />
              </svg>
            </div>
            <h3 className="mb-3 text-success font-extrabold">{t("request_demo_success_title")}</h3>
            <p className="text-muted font_16">{t("request_demo_success")}</p>
            <button className="btn btn-main-color font-15 mt-4" onClick={onClose}>{t("close")}</button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default RequestDemoModal;
